import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { initErrorReporter } from "./lib/errorReporter";
initErrorReporter();
import { CartProvider } from "./context/CartContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CustomerAuthProvider } from "./context/CustomerAuthContext";
import { Toaster } from "./components/ui/sonner";

/* ── Public pages (eagerly loaded) ── */
import HomePage from "./pages/HomePage";
import CategoryPage from "./pages/CategoryPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmPage from "./pages/OrderConfirmPage";
import CmsPage from "./pages/CmsPage";
import CustomerAuthPage from "./pages/CustomerAuthPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import CustomerDashboard from "./pages/CustomerDashboard";
import AutoLoginPage from "./pages/AutoLoginPage";

/* ── Secondary pages (lazy loaded) ── */
const LoxonePage = React.lazy(() => import("./pages/LoxonePage"));
const FVEPage = React.lazy(() => import("./pages/FVEPage"));
const ServicesPage = React.lazy(() => import("./pages/ServicesPage"));
const BlogPage = React.lazy(() => import("./pages/BlogPage"));
const BlogDetailPage = React.lazy(() => import("./pages/BlogDetailPage"));
const CarportsPage = React.lazy(() => import("./pages/CarportsPage"));
const NabijStanicePage = React.lazy(() => import("./pages/NabijStanicePage"));
const RentalPage = React.lazy(() => import("./pages/RentalPage"));
const FVEConfiguratorPage = React.lazy(() => import("./pages/FVEConfiguratorPage"));
const TrackingPage = React.lazy(() => import("./pages/TrackingPage"));

/* ── Admin pages (lazy loaded - only fetched when admin logs in) ── */
const AdminLogin = React.lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = React.lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = React.lazy(() => import("./pages/admin/AdminProducts"));
const AdminCategories = React.lazy(() => import("./pages/admin/AdminCategories"));
const AdminOrders = React.lazy(() => import("./pages/admin/AdminOrders"));
const AdminCustomers = React.lazy(() => import("./pages/admin/AdminCustomers"));
const AdminLeads = React.lazy(() => import("./pages/admin/AdminLeads"));
const AdminImport = React.lazy(() => import("./pages/admin/AdminImport"));
const AdminBlog = React.lazy(() => import("./pages/admin/AdminBlog"));
const AdminSettings = React.lazy(() => import("./pages/admin/AdminSettings"));
const AdminTheme = React.lazy(() => import("./pages/admin/AdminTheme"));
const AdminTickets = React.lazy(() => import("./pages/admin/AdminTickets"));
const AdminCms = React.lazy(() => import("./pages/admin/AdminCms"));
const AdminBanners = React.lazy(() => import("./pages/admin/AdminBanners"));
const AdminBrands = React.lazy(() => import("./pages/admin/AdminBrands"));
const AdminClaims = React.lazy(() => import("./pages/admin/AdminClaims"));
const AdminReturns = React.lazy(() => import("./pages/admin/AdminReturns"));
const AdminInvoices = React.lazy(() => import("./pages/admin/AdminInvoices"));
const AdminStock = React.lazy(() => import("./pages/admin/AdminStock"));
const AdminPricing = React.lazy(() => import("./pages/admin/AdminPricing"));
const AdminPromotions = React.lazy(() => import("./pages/admin/AdminPromotions"));
const AdminWatchdog = React.lazy(() => import("./pages/admin/AdminWatchdog"));
const AdminBetterPrice = React.lazy(() => import("./pages/admin/AdminBetterPrice"));
const AdminNavSettings = React.lazy(() => import("./pages/admin/AdminNavSettings"));
const AdminPromoCards = React.lazy(() => import("./pages/admin/AdminPromoCards"));
const AdminProductAudit = React.lazy(() => import("./pages/admin/AdminProductAudit"));
const AdminAiSettings = React.lazy(() => import("./pages/admin/AdminAiSettings"));
const AdminTeam = React.lazy(() => import("./pages/admin/AdminTeam"));
const AdminChatbot = React.lazy(() => import("./pages/admin/AdminChatbot"));
const AdminPriceGroups = React.lazy(() => import("./pages/admin/AdminPriceGroups"));
const AdminImageStorage = React.lazy(() => import("./pages/admin/AdminImageStorage"));
const AdminXmlFeeds = React.lazy(() => import("./pages/admin/AdminXmlFeeds"));
const AdminDesigner = React.lazy(() => import("./pages/admin/AdminDesigner"));
const AdminPdfDesigner = React.lazy(() => import("./pages/admin/AdminPdfDesigner"));
const AdminCDNPerformance = React.lazy(() => import("./pages/admin/AdminCDNPerformance"));
const AdminEmailTemplates = React.lazy(() => import("./pages/admin/AdminEmailTemplates"));
const AdminCurrencies = React.lazy(() => import("./pages/admin/AdminCurrencies"));
const AdminShipping = React.lazy(() => import("./pages/admin/AdminShipping"));
const AdminPaymentMethods = React.lazy(() => import("./pages/admin/AdminPaymentMethods"));
const AdminOrderSettings = React.lazy(() => import("./pages/admin/AdminOrderSettings"));
const AdminMonitoring = React.lazy(() => import("./pages/admin/AdminMonitoring"));
const AdminAnalytics = React.lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminSeo = React.lazy(() => import("./pages/admin/AdminSeo"));
const AdminSystemSettings = React.lazy(() => import("./pages/admin/AdminSystemSettings"));
const AdminSecurity = React.lazy(() => import("./pages/admin/AdminSecurity"));
const AdminABTesting = React.lazy(() => import("./pages/admin/AdminABTesting"));
const AdminAuditLog = React.lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminConfigurator = React.lazy(() => import("./pages/admin/AdminConfigurator"));
const AdminFveServices = React.lazy(() => import("./pages/admin/AdminFveServices"));
const AdminErrorLog = React.lazy(() => import("./pages/admin/AdminErrorLog"));
const AdminSmsTemplates = React.lazy(() => import("./pages/admin/AdminSmsTemplates"));
const AdminLoyalty = React.lazy(() => import("./pages/admin/AdminLoyalty"));
const AdminQuotations = React.lazy(() => import("./pages/admin/AdminQuotations"));
const AdminPriceCheck = React.lazy(() => import("./pages/admin/AdminPriceCheck"));
const AdminCron = React.lazy(() => import("./pages/admin/AdminCron"));
const AdminWarranties = React.lazy(() => import("./pages/admin/AdminWarranties"));

import ChatbotWidget from "./components/ChatbotWidget";
import FveFloatingButton from "./components/FveFloatingButton";
import PromoPopup from "./components/PromoPopup";
import CookieConsent from "./components/CookieConsent";
import MaintenanceGuard from "./components/MaintenanceGuard";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";

const LazyFallback = () => <div className="min-h-[300px] flex items-center justify-center"><div className="w-8 h-8 border-3 border-green-600 border-t-transparent rounded-full animate-spin" /></div>;

import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";

import "./App.css";

const GatedRoute = ({ children, loginFallback }) => {
    const { token } = useAuth();
    const hasGate = (() => {
        try {
            const gate = sessionStorage.getItem("sg_admin_gate");
            if (!gate) return false;
            const formatter = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Prague", year: "numeric", month: "2-digit", day: "2-digit" });
            const parts = formatter.formatToParts(new Date());
            const dd = String(parseInt(parts.find(p => p.type === "day").value)).padStart(2, "0");
            const mm = String(parseInt(parts.find(p => p.type === "month").value)).padStart(2, "0");
            const yyyy = parts.find(p => p.type === "year").value;
            return gate === `${dd}-${mm}-${yyyy}`;
        } catch { return false; }
    })();
    if (!hasGate) return <Navigate to="/" replace />;
    if (!token) return loginFallback;
    return children;
};

function App() {
    return (
        <ErrorBoundary>
        <AuthProvider>
            <ThemeProvider>
                <CartProvider>
                    <CustomerAuthProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <MaintenanceGuard>
                        <Suspense fallback={<LazyFallback />}>
                        <Routes>
                            {/* Public routes */}
                            <Route path="/" element={<HomePage />} />
                            <Route path="/kategorie" element={<CategoryPage />} />
                            <Route path="/kategorie/:slug" element={<CategoryPage />} />
                            <Route path="/produkt/:slug" element={<ProductDetailPage />} />
                            <Route path="/kosik" element={<CartPage />} />
                            <Route path="/pokladna" element={<CheckoutPage />} />
                            <Route path="/dekujeme" element={<OrderConfirmPage />} />
                            <Route path="/loxone" element={<LoxonePage />} />
                            <Route path="/fve" element={<FVEPage />} />
                            <Route path="/sluzby" element={<ServicesPage />} />
                            <Route path="/blog" element={<BlogPage />} />
                            <Route path="/blog/:postId" element={<BlogDetailPage />} />
                            <Route path="/carporty" element={<CarportsPage />} />
                            <Route path="/nabijeci-stanice" element={<NabijStanicePage />} />
                            <Route path="/pujcovna" element={<RentalPage />} />
                            <Route path="/konfigurator" element={<FVEConfiguratorPage />} />
                            <Route path="/sledovani" element={<TrackingPage />} />
                            <Route path="/stranka/:slug" element={<CmsPage />} />
                            <Route path="/hledat" element={<CategoryPage />} />

                            {/* Customer auth & dashboard */}
                            <Route path="/prihlaseni" element={<CustomerAuthPage />} />
                            <Route path="/zapomenute-heslo" element={<ForgotPasswordPage />} />
                            <Route path="/reset-hesla" element={<ResetPasswordPage />} />
                            <Route path="/muj-ucet" element={<CustomerDashboard />} />
                            <Route path="/auto-prihlaseni" element={<AutoLoginPage />} />

                            {/* Primary admin access: /vstup/DD-MM-RRRR (Prague date) */}
                            <Route path="/vstup/:accessCode" element={<AdminAccessRedirect />} />
                            <Route path="/vstup/:accessCode/*" element={<AdminAccessRedirect />} />

                            {/* Protected admin routes - gate + login fallback */}
                            <Route path="/sprava/*" element={
                                <GatedRoute loginFallback={<AdminLogin />}>
                                    <AdminLayout />
                                </GatedRoute>
                            }>
                                <Route index element={<AdminDashboard />} />
                                <Route path="produkty" element={<AdminProducts />} />
                                <Route path="kategorie" element={<AdminCategories />} />
                                <Route path="objednavky" element={<AdminOrders />} />
                                <Route path="zakaznici" element={<AdminCustomers />} />
                                <Route path="leady" element={<AdminLeads />} />
                                <Route path="import" element={<AdminImport />} />
                                <Route path="blog" element={<AdminBlog />} />
                                <Route path="nastaveni" element={<AdminSettings />} />
                                <Route path="tema" element={<AdminTheme />} />
                                <Route path="komunikace" element={<AdminTickets />} />
                                <Route path="stranky" element={<AdminCms />} />
                                <Route path="bannery" element={<AdminBanners />} />
                                <Route path="znacky" element={<AdminBrands />} />
                                <Route path="reklamace" element={<AdminClaims />} />
                                <Route path="zaruky" element={<AdminWarranties />} />
                                <Route path="vraceni" element={<AdminReturns />} />
                                <Route path="faktury" element={<AdminInvoices />} />
                                <Route path="sklad" element={<AdminStock />} />
                                <Route path="cenotvorba" element={<AdminPricing />} />
                                <Route path="akce" element={<AdminPromotions />} />
                                <Route path="hlidaci-pes" element={<AdminWatchdog />} />
                                <Route path="lepsi-cena" element={<AdminBetterPrice />} />
                                <Route path="navigace" element={<AdminNavSettings />} />
                                <Route path="promo-karty" element={<AdminPromoCards />} />
                                <Route path="audit" element={<AdminProductAudit />} />
                                <Route path="ai-nastaveni" element={<AdminAiSettings />} />
                                <Route path="tym" element={<AdminTeam />} />
                                <Route path="chatbot" element={<AdminChatbot />} />
                                <Route path="cenove-skupiny" element={<AdminPriceGroups />} />
                                <Route path="uloziste-obrazku" element={<AdminImageStorage />} />
                                <Route path="xml-feedy" element={<AdminXmlFeeds />} />
                                <Route path="designer" element={<AdminDesigner />} />
                                <Route path="pdf-designer" element={<AdminPdfDesigner />} />
                                <Route path="cdn-performance" element={<AdminCDNPerformance />} />
                                <Route path="emaily" element={<AdminEmailTemplates />} />
                                <Route path="meny" element={<AdminCurrencies />} />
                                <Route path="doprava" element={<AdminShipping />} />
                                <Route path="platby" element={<AdminPaymentMethods />} />
                                <Route path="kosik-objednavky" element={<AdminOrderSettings />} />
                                <Route path="monitoring" element={<AdminMonitoring />} />
                                <Route path="analytika" element={<AdminAnalytics />} />
                                <Route path="seo" element={<AdminSeo />} />
                                <Route path="kontrola-cen" element={<AdminPriceCheck />} />
                                <Route path="system" element={<AdminSystemSettings />} />
                                <Route path="zabezpeceni" element={<AdminSecurity />} />
                                <Route path="ab-testing" element={<AdminABTesting />} />
                                <Route path="audit-log" element={<AdminAuditLog />} />
                                <Route path="konfigurator" element={<AdminConfigurator />} />
                                <Route path="sluzby-fve" element={<AdminFveServices />} />
                                <Route path="diagnostika" element={<AdminErrorLog />} />
                                <Route path="sms" element={<AdminSmsTemplates />} />
                                <Route path="vernostni-program" element={<AdminLoyalty />} />
                                <Route path="nabidky" element={<AdminQuotations />} />
                                <Route path="cron" element={<AdminCron />} />
                            </Route>

                            {/* Fallback */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                        </Suspense>
                        </MaintenanceGuard>
                        <Toaster richColors position="top-right" />
                        <ChatbotWidget />
                        <FveFloatingButton />
                        <PromoPopup />
                        <CookieConsent />
                    </BrowserRouter>
                    </CustomerAuthProvider>
                </CartProvider>
            </ThemeProvider>
        </AuthProvider>
        </ErrorBoundary>
    );
}

function AdminAccessRedirect() {
    const { accessCode } = useParams();

    // Prague timezone (Europe/Prague)
    const formatter = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/Prague",
        year: "numeric", month: "2-digit", day: "2-digit"
    });
    const parts = formatter.formatToParts(new Date());
    const dd = parseInt(parts.find(p => p.type === "day").value);
    const mm = parseInt(parts.find(p => p.type === "month").value);
    const yyyy = parseInt(parts.find(p => p.type === "year").value);
    const ddStr = String(dd).padStart(2, "0");
    const mmStr = String(mm).padStart(2, "0");

    // Primary format: DD-MM-RRRR (e.g., 03-04-2026)
    const todaySlug = `${ddStr}-${mmStr}-${yyyy}`;
    // Emergency format: sum of DD+MM+YYYY (e.g., 03+04+2026=2033)
    const emergencySlug = String(dd + mm + yyyy);

    if (accessCode === todaySlug || accessCode === emergencySlug) {
        // Mark session as valid admin entry
        try { sessionStorage.setItem("sg_admin_gate", todaySlug); } catch {}
        const fullPath = window.location.pathname;
        const prefix = `/vstup/${accessCode}`;
        const subPath = fullPath.startsWith(prefix) ? fullPath.slice(prefix.length) : "";
        return <Navigate to={`/sprava${subPath || ""}`} replace />;
    }
    return <Navigate to="/" replace />;
}

function AppWrapper() {
    return (
        <HelmetProvider>
            <QueryClientProvider client={queryClient}>
                <App />
            </QueryClientProvider>
        </HelmetProvider>
    );
}

export default AppWrapper;
